import { Component, useContext, createContext } from 'react';

import { UserInfo, UserInfoData, UserInfoProps } from 'etc/types';
import { SP_API_URL } from 'lib/shared/config/config';
import { captureException } from 'lib/client/sentry/sentry';
import auth from 'lib/shared/auth/auth';

export const UserInfoContext = createContext<UserInfo>({} as UserInfo);
export const useUserInfo = () => useContext(UserInfoContext);

export const withUserInfo = <P extends UserInfoProps>(Comp: React.ComponentType<P>) => {
  return (props: Omit<P, keyof UserInfoProps>) => (
    <UserInfoContext.Consumer>{userInfo => <Comp {...(props as P)} userInfo={userInfo} />}</UserInfoContext.Consumer>
  );
};

class UserInfoProvider extends Component<{}, UserInfo> {
  constructor(props: {}) {
    super(props);

    this.state = {
      loading: true,
      error: false,
      data: null,
    };
  }

  async componentDidMount() {
    try {
      const response = await auth.fetch(`${SP_API_URL}/v1/me`, {});
      const data: SpotifyApi.CurrentUsersProfileResponse = await response.json();

      this.setState({
        loading: false,
        error: false,
        data: {
          username: data.id,
          displayName: data.display_name,
          email: data.email,
          avatar: data.images?.[0] && data.images[0].url,
          followerCount: data.followers ? data.followers.total : 0,
          product: data.product as UserInfoData['product'],
        },
      });
    } catch (e) {
      // If we are on slingshot, auth error will be treated as "anonymous"
      if (e.error === 'login_required' || process.env.REACT_APP_IS_SLINGSHOT === 'true') {
        this.setState({ loading: false });
        return;
      }

      captureException(e);
      this.setState({ loading: false, error: true });
    }
  }

  render() {
    const { loading, error, data } = this.state;
    const contextValue = {
      loading,
      error,
      data,
    };

    return <UserInfoContext.Provider value={contextValue}>{this.props.children}</UserInfoContext.Provider>;
  }
}

export default UserInfoProvider;
